import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { peekJa, strings, subscribeJa, type Lang } from '@/app/i18n/strings';
import { LangContext } from '@/app/context/lang-context';

const listeners = new Set<() => void>();

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  window.addEventListener('storage', onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
}

function emitLang() {
  listeners.forEach((listener) => listener());
}

function readLang(): Lang {
  try {
    return localStorage.getItem('nsr_lang') === 'ja' ? 'ja' : 'en';
  } catch {
    return 'en';
  }
}

function persistLang(lang: Lang) {
  try {
    localStorage.setItem('nsr_lang', lang);
    return true;
  } catch {
    return false;
  }
}

const serverLang = (): Lang => 'en';

export function LangProvider({ children }: { children: ReactNode }) {
  const lang = useSyncExternalStore(subscribe, readLang, serverLang);
  const loadedJa = useSyncExternalStore(subscribeJa, peekJa, () => null);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.classList.toggle('lang-ja', lang === 'ja');
  }, [lang]);

  useEffect(() => {
    if (lang !== 'ja' || peekJa()) return;
    void import('@/app/i18n/ja');
  }, [lang]);

  const activeJa = lang === 'ja' ? loadedJa : null;
  const t = (key: string): string => activeJa?.[key] ?? strings.en[key] ?? key;

  const toggle = () => {
    const next = readLang() === 'en' ? 'ja' : 'en';
    persistLang(next);
    emitLang();
  };

  return (
    <LangContext.Provider value={{ lang, t, toggle }}>
      {children}
    </LangContext.Provider>
  );
}
